import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, cn, interpolateCopy, usePublicShareRouteParam } from '@yeshuman/sdk'

import { yeshumanAnn, yeshumanAnnDim, yeshumanEditorialSm, yeshumanUiSans } from './typography'
import { WORKING_SESSION_RULES, WORKING_SESSION_VOICE_FAIL } from './working-session'
import {
  endWorkingSession,
  fetchSessionGate,
  fetchSessionTable,
  markSessionReady,
  requestSessionOtp,
  streamWorkingSessionTurn,
  verifySessionOtp,
  type WorkingSessionGate,
  type WorkingSessionTable,
} from './working-session-api'
import { WorkingSessionShell } from './working-session-shell'
import { WorkingSessionTableView } from './working-session-table'

type RoomState = 'loading' | 'otp' | 'checkout' | 'live' | 'packet' | 'missing'

const GREETING =
  'Hello {name}. This is a working session on {company}. We get the problem and what you want, then map five layers, then maybe name a possible first slice — not a promise. Talk or type. Captions on. Pause and repeat anytime. The brief stays on the table. No pitch. You leave with this table.'

export function WorkingSessionPage({ tokenOverride }: { tokenOverride?: string } = {}) {
  const token = usePublicShareRouteParam('/session/:token', 'token', tokenOverride)
  const [room, setRoom] = useState<RoomState>('loading')
  const [gate, setGate] = useState<WorkingSessionGate | null>(null)
  const [table, setTable] = useState<WorkingSessionTable | null>(null)
  const [error, setError] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpBusy, setOtpBusy] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [caption, setCaption] = useState('')
  const [draft, setDraft] = useState('')
  const [lastCaption, setLastCaption] = useState('')
  const [paused, setPaused] = useState(false)
  const [voiceFailed, setVoiceFailed] = useState(true)
  const [otpRequested, setOtpRequested] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!token) {
      setRoom('missing')
      return
    }
    let cancelled = false
    async function boot() {
      try {
        const nextGate = await fetchSessionGate(token)
        if (cancelled) return
        setGate(nextGate)
        try {
          const nextTable = await fetchSessionTable(token)
          if (cancelled) return
          setTable(nextTable)
          setRoom(nextTable.ended ? 'packet' : nextTable.status === 'live' ? 'live' : 'checkout')
        } catch {
          if (!cancelled) setRoom('otp')
        }
      } catch {
        if (!cancelled) setRoom('missing')
      }
    }
    void boot()
    return () => {
      cancelled = true
    }
  }, [token])

  useEffect(() => {
    if (cooldown <= 0) return
    const id = window.setTimeout(() => setCooldown((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearTimeout(id)
  }, [cooldown])

  const greeting = useMemo(() => {
    if (!gate) return GREETING
    return interpolateCopy(GREETING, {
      name: gate.name || 'there',
      company: gate.company || 'your project',
    })
  }, [gate])

  useEffect(() => {
    if (room === 'checkout') {
      setCaption(greeting)
      setLastCaption(greeting)
    }
  }, [room, greeting])

  const requestOtp = useCallback(async () => {
    if (!token) return
    setError('')
    setOtpBusy(true)
    try {
      const result = await requestSessionOtp(token)
      setCooldown(result.cooldown_seconds)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a code')
    } finally {
      setOtpBusy(false)
    }
  }, [token])

  useEffect(() => {
    if (room !== 'otp' || otpRequested || !token) return
    setOtpRequested(true)
    void requestOtp()
  }, [room, otpRequested, token, requestOtp])

  const verifyOtp = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!token) return
    setError('')
    setOtpBusy(true)
    try {
      const result = await verifySessionOtp(token, otpCode)
      const nextTable = await fetchSessionTable(token)
      setTable(nextTable)
      setRoom(result.ended ? 'packet' : 'checkout')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code is not right')
    } finally {
      setOtpBusy(false)
    }
  }

  const sendTurn = async (message: string) => {
    if (!token || paused || sending) return
    const text = message.trim()
    if (!text) return
    setCaption(text)
    setLastCaption(text)
    setSending(true)
    setError('')
    let agentText = ''
    try {
      await streamWorkingSessionTurn(token, text, (event) => {
        const type = String(event.type || '')
        if (type === 'caption') {
          agentText += String(event.text || '')
          if (!paused) {
            setCaption(agentText)
            setLastCaption(agentText)
          }
        }
        if (type === 'table' && event.table) {
          setTable(event.table as WorkingSessionTable)
        }
        if (type === 'ended' && event.table) {
          setTable(event.table as WorkingSessionTable)
          setRoom('packet')
        }
        if (type === 'error') {
          setError(String(event.message || WORKING_SESSION_VOICE_FAIL))
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Type here. Same session.')
    } finally {
      setSending(false)
    }
  }

  const onReady = async (firstMessage?: string) => {
    if (!token) return
    setError('')
    const opening = (firstMessage ?? draft).trim()
    try {
      const next = await markSessionReady(token)
      setTable(next)
      setRoom('live')
      if (opening) {
        setDraft('')
        await sendTurn(opening)
      } else {
        setCaption('')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start')
    }
  }

  const onSend = async () => {
    if (paused || sending) return
    const message = draft.trim()
    if (!message) return
    setDraft('')
    if (room === 'checkout') {
      await onReady(message)
      return
    }
    await sendTurn(message)
  }

  const onLeave = async () => {
    if (!token) return
    try {
      const next = await endWorkingSession(token)
      setTable(next)
      setRoom('packet')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not freeze the table')
    }
  }

  if (room === 'loading') {
    return (
      <WorkingSessionShell>
        <p className={yeshumanEditorialSm}>Opening the working session…</p>
      </WorkingSessionShell>
    )
  }

  if (room === 'missing') {
    return (
      <WorkingSessionShell>
        <p className={yeshumanEditorialSm}>This working session link is not available.</p>
      </WorkingSessionShell>
    )
  }

  if (room === 'otp' && gate) {
    return (
      <WorkingSessionShell>
        <div className="mx-auto max-w-md space-y-6 pt-16">
          <p className={cn(yeshumanAnn, yeshumanAnnDim)}>Working session</p>
          <p className={yeshumanEditorialSm}>
            We’ll send a 6-digit code to {gate.masked_email}. Open this page on the same device.
          </p>
          <form className="space-y-4" onSubmit={verifyOtp}>
            {error ? (
              <p className="text-sm text-[var(--marketing-gold)]" role="alert">
                {error}
              </p>
            ) : null}
            <label className="block" htmlFor="working-session-otp">
              <span className={cn(yeshumanAnn, 'mb-2 block')}>Code</span>
              <input
                id="working-session-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpCode}
                onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                className={cn(
                  yeshumanUiSans,
                  'w-full border border-[var(--marketing-border)] bg-[var(--marketing-bg)] px-4 py-3',
                  'text-center text-2xl tracking-[0.4em] outline-none',
                  'focus-visible:border-[var(--marketing-gold)]',
                )}
                aria-label="Six digit code"
              />
            </label>
            <Button type="submit" variant="marketingSolid" size="marketing" className="w-full" disabled={otpBusy}>
              {otpBusy ? 'Checking…' : 'Open session'}
            </Button>
          </form>
          <button
            type="button"
            className={cn(yeshumanUiSans, 'text-sm text-[var(--marketing-gold)] disabled:opacity-50')}
            onClick={() => void requestOtp()}
            disabled={otpBusy || cooldown > 0}
          >
            {cooldown > 0 ? `Send a code again in ${cooldown}s` : 'Email me a code'}
          </button>
        </div>
      </WorkingSessionShell>
    )
  }

  if (room === 'packet' && table) {
    return (
      <WorkingSessionShell stance={table.stance}>
        <div className="space-y-10">
          <p className={yeshumanEditorialSm}>
            Here’s what we heard, the map, and a possible slice — not a commitment.
          </p>
          <WorkingSessionTableView table={table} packet />
        </div>
      </WorkingSessionShell>
    )
  }

  return (
    <WorkingSessionShell stance={table?.stance || 'consultant'}>
      <div className="flex min-h-[calc(100vh-8rem)] flex-col">
        <div className="min-h-0 flex-1">
          {table ? <WorkingSessionTableView table={table} /> : null}
          {room === 'checkout' ? (
            <aside className="mt-10 max-w-[42ch] border-t border-[var(--marketing-border)] pt-6">
              <p className={cn(yeshumanAnn, yeshumanAnnDim)}>On the table</p>
              <p className={cn(yeshumanEditorialSm, 'mt-3')}>{WORKING_SESSION_RULES}</p>
            </aside>
          ) : null}
        </div>
        <div className="sticky bottom-0 mt-10 border-t border-[var(--marketing-border)] bg-[var(--marketing-bg)] pt-4">
          {voiceFailed ? (
            <p className={cn(yeshumanAnn, 'mb-3 text-[var(--marketing-gold)]')} role="status">
              {WORKING_SESSION_VOICE_FAIL}
            </p>
          ) : null}
          {error ? (
            <p className="mb-3 text-sm text-[var(--marketing-gold)]" role="alert">
              {error}
            </p>
          ) : null}
          <div
            className={cn(
              yeshumanUiSans,
              'min-h-[3.5rem] border border-[var(--marketing-border)] px-4 py-3',
              'text-[17px] leading-snug',
            )}
            aria-live="polite"
          >
            {sending || paused ? (
              <p className="line-clamp-2">{paused ? lastCaption : caption || 'Type here. Same session.'}</p>
            ) : (
              <label htmlFor="working-session-caption" className="block">
                <span className="sr-only">Type into the live caption</span>
                <input
                  id="working-session-caption"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault()
                      void onSend()
                    }
                  }}
                  className={cn(
                    yeshumanUiSans,
                    'w-full bg-transparent text-[17px] leading-snug outline-none',
                    'placeholder:text-[var(--yeshuman-ink-subtle,var(--marketing-muted))]',
                  )}
                  placeholder={caption || 'Type here. Same session.'}
                  disabled={paused || sending}
                />
              </label>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <DeskButton label="Pause" onClick={() => setPaused((value) => !value)} active={paused} />
            <DeskButton label="Repeat" onClick={() => setCaption(lastCaption)} />
            <DeskButton
              label="Mic"
              onClick={() => {
                setVoiceFailed(true)
                setError(WORKING_SESSION_VOICE_FAIL)
              }}
            />
            {room === 'checkout' ? (
              <Button variant="marketingSolid" size="marketing" onClick={() => void onReady()}>
                Ready
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="marketingSolid"
                  size="marketing"
                  disabled={paused || sending || !draft.trim()}
                  onClick={() => void onSend()}
                >
                  {sending ? 'Listening…' : 'Send'}
                </Button>
                <Button type="button" variant="outline" onClick={() => void onLeave()}>
                  Leave with this table
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </WorkingSessionShell>
  )
}

function DeskButton({
  label,
  onClick,
  active = false,
}: {
  label: string
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        yeshumanUiSans,
        'text-sm tracking-wide',
        active ? 'text-[var(--marketing-gold)]' : 'text-[var(--yeshuman-ink-subtle,var(--marketing-muted))]',
      )}
    >
      {label}
    </button>
  )
}
